/**
 * Copyright IBM Corp. 2022, 2026
 */

import { createContext, useContext, useMemo } from 'react'
import { usePathname } from 'lib/i18n/routing'
import { isMenuType } from './helpers/determine-item-type'
import type {
	SidebarTopLevelProps,
	SidebarMenuItemProps,
	SidebarLinkItemProps,
	SidebarSection,
} from './types'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'

export interface SidebarState {
	activeSection: SidebarSection | null
	activeLink: SidebarLinkItemProps | null
	activeMenu: SidebarMenuItemProps | null
}

interface SidebarProviderProps {
	children: React.ReactNode
	sections: Array<SidebarSection>
}

const SidebarContext = createContext<SidebarState | undefined>(undefined)

const SidebarProvider = ({ children, sections }: SidebarProviderProps) => {
	const pathname = usePathname()

	// Remove anchor from asPath if one exists
	const asPath = useMemo(() => {
		// we use example.com as the hostname here because URL requires a
		// hostname when it runs on Node.js. Since we're only looking at the
		// pathname, the hostname doesn't matter.
		const url = new URL(`http://example.com${pathname}`)
		return url.pathname
	}, [pathname])

	const hasActiveItem = (item: SidebarTopLevelProps) => {
		return isMenuType(item) ? hasActiveMenu(item) : isActiveLink(item)
	}

	const hasActiveMenu = (item: SidebarMenuItemProps) => {
		return item.routes.some(isActiveLink)
	}

	const isActiveLink = (item: SidebarLinkItemProps) => {
		return parseUrl(item.href).href === asPath
	}

	/**
	 * The sidebar section that contains the active link
	 * Should always be defined
	 */
	const activeSection = useMemo<SidebarSection | null>(
		() => sections.find((section) => section.sectionItems.some(hasActiveItem)),
		[asPath],
	)

	/**
	 * The menu that contains the active link
	 * Is only defined if activeLink is nested in a routes array
	 */
	const activeMenu = useMemo<SidebarMenuItemProps | null>(() => {
		if (!activeSection) {
			return null
		}

		const topLevel = activeSection.sectionItems.find(hasActiveItem)

		if (isMenuType(topLevel)) {
			return topLevel
		}

		return null
	}, [activeSection, asPath])

	/**
	 * The link whose href matches the current path
	 * Should always be defined
	 */
	const activeLink = useMemo<SidebarLinkItemProps | null>(() => {
		if (!activeSection) {
			return null
		}

		const topLevel = activeSection.sectionItems.find(hasActiveItem)

		if (isMenuType(topLevel)) {
			return topLevel.routes.find(isActiveLink)
		}

		return topLevel
	}, [activeSection, asPath])

	const contextValue = useMemo(
		() => ({ activeSection, activeLink, activeMenu }),
		[activeSection, activeLink, activeMenu],
	)

	return (
		<SidebarContext.Provider value={contextValue}>
			{children}
		</SidebarContext.Provider>
	)
}

export function useSidebar() {
	const context = useContext(SidebarContext)

	if (context === undefined) {
		throw new Error('useSidebar must be used within a SidebarProvider')
	}

	return context
}

export { SidebarProvider }
